/**
 * ruleGlossary.ts
 *
 * Plain-language guide to the Title analysis rules, for the Help panel on the
 * Title analysis screen. Each exception in a report carries one of these rule
 * codes (VC = vesting chain, EN = encumbrance, CP = completeness).
 *
 * The rules are not written here. They come from the same JSON file the backend
 * reads for its severity policy and the rule definitions in the agent's prompt
 * (`backend/lambdas/ai_agent/agents/title_analysis/rules.json`), so a change to
 * a rule reaches the Help panel and the agent in the same deployment. Edit that
 * file, not this one.
 *
 * No React and no I/O.
 */

import rulesFile from '../../../backend/lambdas/ai_agent/agents/title_analysis/rules.json';

export type RuleSeverity = 'material' | 'curable' | 'informational';

export interface RuleEntry {
  code: string;
  /** Short name, as a reader would say it. */
  title: string;
  severity: RuleSeverity;
  /** What the rule checks and what it means when it appears in a report. */
  meaning: string;
  /** An exception to the fixed severity, or a point worth knowing. */
  note?: string;
}

export interface RuleGroup {
  id: 'vesting' | 'encumbrance' | 'completeness';
  label: string;
  summary: string;
  rules: RuleEntry[];
}

export const SEVERITY_DEFINITIONS: ReadonlyArray<{
  severity: RuleSeverity;
  label: string;
  meaning: string;
}> = rulesFile.severity_levels.map((level) => ({
  severity: level.id as RuleSeverity,
  label: level.label,
  meaning: level.meaning,
}));

export const RULE_GROUPS: ReadonlyArray<RuleGroup> = rulesFile.groups.map((group) => ({
  id: group.id as RuleGroup['id'],
  label: group.label,
  summary: group.summary,
  rules: group.rules.map((rule) => ({
    code: rule.code,
    title: rule.title,
    severity: rule.severity as RuleSeverity,
    meaning: rule.meaning,
    ...('note' in rule && typeof rule.note === 'string' ? { note: rule.note } : {}),
  })),
}));

/** Shown when a title commitment or policy was supplied. */
export const COMMITMENT_GUIDE: {
  label: string;
  summary: string;
  points: ReadonlyArray<string>;
} = {
  label: rulesFile.commitment.label,
  summary: rulesFile.commitment.summary,
  points: rulesFile.commitment.points,
};

/** Every rule, in display order. */
export const ALL_RULES: ReadonlyArray<RuleEntry> = RULE_GROUPS.flatMap((g) => g.rules);
